import React, { useState, useMemo, useRef, useEffect } from 'react';
import { specialisationName } from '../../constants/staff';
import './DoctorPicker.css';

/**
 * DoctorPicker — custom combobox with avatar + search.
 *
 * Replaces the native <select> for picking a doctor on admin screens.
 * Click the trigger to open a popup with a search box and a list of
 * doctors rendered as avatar + name + specialisation. Click-outside and
 * Escape both close it.
 *
 * Props:
 *   doctors           — [{ member_id, first_name, last_name, specialization?,
 *                          username?, registration_no? }, ...]
 *   selectedId        — currently selected member_id (or null/'')
 *   onChange(id)      — called with the new member_id (or null for 'clear')
 *   isLoading         — disables the trigger + shows a loading label
 *   placeholder       — override for the default "Select a doctor..." text
 *   searchPlaceholder — override for the search input's placeholder
 *                       (default "Search by name, specialty, or reg no…")
 *   clearLabel        — override for the clear-selection button's label
 *                       (default "✕ Clear selection")
 *   showSubline       — false hides the trigger/item sub-line (specialty,
 *                       falling back to "General") entirely (default true —
 *                       byte-identical to every existing caller)
 *   onTypeOther       — when given, a dashed "type a name" row renders above
 *                       the footer; clicking it closes the popup and calls
 *                       onTypeOther() (the caller swaps to a text input for a
 *                       doctor who is not on the list). Absent → no row,
 *                       byte-identical to every existing caller.
 *   typeOtherLabel    — label of that row (default "＋ Type another name")
 */

// Small palette; avatar colour is picked deterministically from the
// doctor's member_id so every doctor keeps the same colour across renders
// without needing extra state.
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
  'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
  'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  'linear-gradient(135deg, #10b981 0%, #047857 100%)',
];

function pickGradient(memberId) {
  if (!memberId) return AVATAR_GRADIENTS[0];
  let h = 0;
  for (let i = 0; i < memberId.length; i++) h = (h * 31 + memberId.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

function doctorDisplayName(d) {
  const full = [d.first_name, d.last_name].filter(Boolean).join(' ').trim();
  return full || d.username || d.member_id;
}

function doctorInitial(d) {
  const n = doctorDisplayName(d);
  return (n[0] || '?').toUpperCase();
}

function Avatar({ doctor, size = 36 }) {
  return (
    <div
      className="dp-avatar"
      style={{
        width: size,
        height: size,
        background: pickGradient(doctor?.member_id),
        fontSize: Math.round(size * 0.42),
      }}
      aria-hidden
    >
      {doctor ? doctorInitial(doctor) : '?'}
    </div>
  );
}

export default function DoctorPicker({
  doctors = [],
  selectedId = null,
  onChange,
  isLoading = false,
  placeholder = 'Select a doctor…',
  searchPlaceholder = 'Search by name, specialty, or reg no…',
  clearLabel = '✕ Clear selection',
  showSubline = true,
  onTypeOther,
  typeOtherLabel = '＋ Type another name',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = useMemo(
    () => doctors.find((d) => d.member_id === selectedId) || null,
    [doctors, selectedId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) => {
      const name = doctorDisplayName(d).toLowerCase();
      const spec = (d.specialization || '').toLowerCase();
      const reg = (d.registration_no || '').toLowerCase();
      return name.includes(q) || spec.includes(q) || reg.includes(q);
    });
  }, [doctors, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape, focus search on open
  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handler);
    // Next tick so the input exists
    setTimeout(() => searchRef.current?.focus(), 0);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const choose = (id) => {
    setOpen(false);
    setQuery('');
    onChange?.(id);
  };

  return (
    <div className="dp-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`dp-trigger ${open ? 'dp-trigger-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <>
            <Avatar doctor={selected} />
            <div className="dp-trigger-meta">
              <div className="dp-trigger-name">{doctorDisplayName(selected)}</div>
              {showSubline && selected.specialization && (
                <div className="dp-trigger-sub">
                  {/* FE-35 — the speciality's NAME, not the stored value. */}
                  {specialisationName(selected.specialization) || selected.specialization}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="dp-avatar dp-avatar-empty" aria-hidden>?</div>
            <div className="dp-trigger-meta">
              <div className="dp-trigger-name dp-trigger-placeholder">
                {isLoading ? 'Loading doctors…' : placeholder}
              </div>
            </div>
          </>
        )}
        <span className={`dp-chevron ${open ? 'dp-chevron-up' : ''}`} aria-hidden>▾</span>
      </button>

      {open && (
        <div className="dp-popup" role="listbox">
          <div className="dp-search-wrap">
            <input
              ref={searchRef}
              type="text"
              className="dp-search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {selected && (
            <button
              type="button"
              className="dp-clear"
              onClick={() => choose(null)}
            >
              {clearLabel}
            </button>
          )}

          <div className="dp-list">
            {filtered.length === 0 ? (
              <div className="dp-empty">
                {doctors.length === 0 ? 'No active doctors yet.' : 'No matches.'}
              </div>
            ) : (
              filtered.map((d) => {
                const isSel = d.member_id === selectedId;
                return (
                  <button
                    key={d.member_id}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`dp-item ${isSel ? 'dp-item-selected' : ''}`}
                    onClick={() => choose(d.member_id)}
                  >
                    <Avatar doctor={d} size={32} />
                    <div className="dp-item-meta">
                      <div className="dp-item-name">{doctorDisplayName(d)}</div>
                      {showSubline && (
                        <div className="dp-item-sub">
                          {specialisationName(d.specialization) || d.specialization || 'General'}
                          {d.registration_no && ` · Reg ${d.registration_no}`}
                        </div>
                      )}
                    </div>
                    {isSel && <span className="dp-tick">✓</span>}
                  </button>
                );
              })
            )}
          </div>

          {onTypeOther && (
            <button
              type="button"
              className="dp-typeown"
              onClick={() => { setOpen(false); setQuery(''); onTypeOther(); }}
            >
              {typeOtherLabel}
            </button>
          )}

          <div className="dp-footer">
            {filtered.length} of {doctors.length} doctor{doctors.length === 1 ? '' : 's'}
          </div>
        </div>
      )}
    </div>
  );
}
